// D98.2 / D183.4 - Profitability tab. Internal only (never printed, D96.13).
// ACTUAL figures only: the profit on the lines a supplier's cost has costed (billed) or a vendor bill being entered has
// priced (entered). A line still on the PO or quoted price (waiting) has no cost, mark-up or margin here: an estimate is
// never shown as a cost. The tab works out nothing: every figure is a field of the ONE `OrderProgress` the page worked
// out (orderProgress.ts, BUSINESS_RULES#client-invoice-live-progress), the one the header strip reads too.
// Props:
//   progress  = the page's orderProgress (lines, totals, `final`)
//   summary   = only the stored marginSanityWarning (D146: that sanity check stays gross-based, the shared owner
//               Accounts and the PDF also read)
//   currency  = invoice currency code (blank allowed: a bare number is shown, D96.14)
//   invoiceId = D119.13: lets a US$ invoice also show the totals in TT$, at the rate pinned on it
import React, { useEffect, useState } from 'react';
import { fetchInvoicePinnedRate } from '../../lib/clientInvoiceMarginApi';
import { buildTtdFigures, moneyOrBlank, pctOrBlank } from './profitabilityRows';
import type { LineProgress, LineProgressState, OrderProgress } from './orderProgress';

export type ProfitabilitySummary = {
  marginSanityWarning?: string | null;
};

type Props = {
  progress: OrderProgress;
  summary: ProfitabilitySummary;
  currency?: string | null;
  invoiceId?: string | null;
};

// D161.8 / D63: the supplier VAT USC pays is a real cost, so this cost INCLUDES it while supplier PO prices exclude it.
export const COST_INCLUDES_VAT_NOTE =
  'Includes the VAT USC paid to its suppliers (input VAT, D63). The vessel is not charged VAT. Supplier PO prices exclude VAT, so VAT-rated lines are higher here.';

const tone = (v: number | null | undefined) => (typeof v !== 'number' ? 'text-slate-500' : v < 0 ? 'text-rose-600' : 'text-slate-800');

const STATUS: Record<LineProgressState, { label: string; className: string }> = {
  billed: { label: 'Billed', className: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  entered: { label: 'Entered', className: 'border-amber-200 bg-amber-50 text-amber-800' },
  waiting: { label: 'Waiting for bill', className: 'border-rose-200 bg-rose-50 text-rose-800' },
  closed: { label: 'Closed', className: 'border-slate-200 bg-slate-100 text-slate-600' },
  hand: { label: 'Typed by hand', className: 'border-slate-300 bg-slate-50 text-slate-700' },
};

/** Waiting lines first, then entered, then the rest; line order inside each group (a stable sort, ties keep their place). */
const WAITING_FIRST: Record<LineProgressState, number> = { waiting: 0, entered: 1, billed: 2, hand: 2, closed: 2 };

const qtyText = (l: LineProgress): string => {
  if (l.orderQty === null) return '--';
  return l.actualQty !== null ? `${l.actualQty} of ${l.orderQty}` : String(l.orderQty);
};

export function ProfitabilityTab({ progress, summary, currency, invoiceId }: Props) {
  const [waitingFirst, setWaitingFirst] = useState(true);
  // TT$ beside US$: the invoice's pinned rate (undefined = still asking, null = none pinned). A TT$ invoice is already in TT$.
  const isUsd = String(currency ?? '').trim().toUpperCase() === 'USD';
  const [rate, setRate] = useState<number | null | undefined>(undefined);
  const [rateFailed, setRateFailed] = useState(false);
  useEffect(() => {
    if (!invoiceId || !isUsd) return undefined;
    let live = true;
    setRate(undefined);
    setRateFailed(false);
    fetchInvoicePinnedRate(invoiceId)
      .then((r) => { if (live) setRate(r.usdToTtd); })
      .catch(() => { if (live) setRateFailed(true); });
    return () => { live = false; };
  }, [invoiceId, isUsd]);
  const ttd = isUsd && rate ? buildTtdFigures(progress, currency, rate) : null;
  const t = (v: number | null | undefined) => moneyOrBlank(v, 'TTD');
  const m = (v: number | null | undefined) => moneyOrBlank(v, currency);

  const lines = waitingFirst
    ? progress.lines.map((l, i) => ({ l, i })).sort((a, b) => WAITING_FIRST[a.l.state] - WAITING_FIRST[b.l.state] || a.i - b.i).map((x) => x.l)
    : progress.lines;

  const figures: Array<[string, string, string?, string?]> = [
    ['Selling', m(progress.selling)],
    ['Actual cost', m(progress.cost), undefined, COST_INCLUDES_VAT_NOTE],
    ['Mark-up', m(progress.margin)],
    ['Margin % vs selling', pctOrBlank(progress.marginPct), summary.marginSanityWarning || undefined],
    ['Mark-up % vs cost', pctOrBlank(progress.markupPct)],
  ];
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
        <div className="mb-2 text-xs font-semibold text-slate-700">
          {progress.final ? 'Final actual profit' : 'Actual profit so far (billed and entered lines)'}
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2" aria-label="Profit figures">
          {figures.map(([label, value, hint, note]) => (
            <div key={label} title={hint || note}>
              <div className="text-xs text-slate-500">{label}</div>
              <div className={`text-sm font-semibold ${hint ? 'text-rose-600' : label === 'Mark-up' ? tone(progress.margin) : 'text-slate-800'}`}>{value}</div>
            </div>
          ))}
        </div>
      </div>
      {summary.marginSanityWarning && <div className="text-xs text-rose-700">{summary.marginSanityWarning}</div>}

      {ttd ? (
        <div className="flex flex-wrap gap-x-8 gap-y-2 rounded-md border border-slate-200 bg-white px-4 py-3" aria-label="In TT$">
          <div className="w-full text-xs text-slate-500">In TT$, at the rate pinned on this invoice (US$1 = TT${rate?.toFixed(4)})</div>
          {([
            ['Selling TT$', ttd.selling],
            ['Actual cost TT$', ttd.cost],
            ['Mark-up TT$', ttd.markup],
          ] as const).map(([label, value]) => (
            <div key={label}>
              <div className="text-xs text-slate-500">{label}</div>
              <div className={`text-sm font-semibold ${tone(value)}`}>{t(value)}</div>
            </div>
          ))}
        </div>
      ) : isUsd && invoiceId && (rate === null || rateFailed) ? (
        <div className="text-xs text-slate-500">
          {rateFailed ? 'The TT$ rate could not be loaded, so TT$ figures are not shown.' : 'No TT$ rate is pinned on this invoice, so TT$ figures are not shown.'}
        </div>
      ) : null}

      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={waitingFirst} onChange={(e) => setWaitingFirst(e.target.checked)} />
        Show waiting first
      </label>

      <div className="max-h-[60vh] overflow-auto rounded-md border border-slate-200 bg-white">
        <table className="data-table-grid w-full text-xs">
          <thead className="sticky top-0 border-b border-slate-200 bg-slate-50">
            <tr className="text-left font-semibold text-slate-600">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Selling</th>
              <th className="px-3 py-2 text-right" title={COST_INCLUDES_VAT_NOTE}>Cost (incl. supplier VAT)</th>
              <th className="px-3 py-2 text-right">Mark-up</th>
              <th className="px-3 py-2 text-right">Margin %</th>
              <th className="px-3 py-2 text-right">Mark-up %</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.lineNumber} className="border-b border-slate-100">
                <td className="px-3 py-2 text-slate-500">{l.lineNumber}</td>
                <td className="max-w-xs truncate px-3 py-2 text-slate-800">{l.label}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium leading-none ${STATUS[l.state].className}`}
                    title={l.state === 'entered' ? `Entered, not confirmed: ${l.enteredFrom.join(', ')}` : undefined}
                  >
                    {STATUS[l.state].label}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{qtyText(l)}</td>
                <td className="px-3 py-2 text-right">{m(l.selling)}</td>
                <td className="px-3 py-2 text-right">{m(l.cost)}</td>
                <td className={`px-3 py-2 text-right font-semibold ${tone(l.margin)}`}>{m(l.margin)}</td>
                <td className={`px-3 py-2 text-right ${tone(l.marginPct)}`}>{pctOrBlank(l.marginPct)}</td>
                <td className={`px-3 py-2 text-right ${tone(l.markupPct)}`}>{pctOrBlank(l.markupPct)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
            <tr>
              <td colSpan={4} className="px-3 py-2 text-slate-700">Total</td>
              <td className="px-3 py-2 text-right">{m(progress.selling)}</td>
              <td className="px-3 py-2 text-right">{m(progress.cost)}</td>
              <td className={`px-3 py-2 text-right ${tone(progress.margin)}`}>{m(progress.margin)}</td>
              <td className="px-3 py-2 text-right">{pctOrBlank(progress.marginPct)}</td>
              <td className="px-3 py-2 text-right">{pctOrBlank(progress.markupPct)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
